import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Inbox, LayoutTemplate, Plus } from 'lucide-react';
import { useState } from 'react';

import { useSiteScope } from '@/app/site/useSiteScope';
import {
  CatalogCard,
  CatalogGrid,
  CatalogHead,
  CatalogSearch,
  CatalogTable,
  NewCard,
  ViewToggle,
} from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import { useSchemaTypes } from '@/features/catalog/useSchemaTypes';
import { useViewMode } from '@/features/catalog/useViewMode';
import { TemplatesDialog } from '@/features/templates/TemplatesDialog';
import { useTemplates } from '@/features/templates/useTemplates';
import type { TypeBody } from '@/shared/api/generated/model';
import { describeWhen } from '@/shared/lib/dates';
import { pluralize } from '@/shared/lib/plural';
import { Button } from '@/shared/ui/Button';
import { Counter } from '@/shared/ui/Counter';
import { EmptyState } from '@/shared/ui/EmptyState';

import { SUBMISSIONS } from './words';

/**
 * «Формы» (решение Давида 23 октября 2026): формы сайта отдельно от данных —
 * сколько заявок, сколько новых, когда пришла последняя. Внутри форма — та же
 * коллекция, что в «Моделях», поэтому поля, правила и перенос — общие.
 */
export function FormsPage() {
  const { siteId, env } = useParams({ from: '/authed/sites/$siteId/$env/forms/' });
  const scope = useSiteScope();
  const navigate = useNavigate();
  const [view, setView] = useViewMode('forms');
  const [search, setSearch] = useState('');
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const shaper = scope?.can('site.shape') ?? false;
  const types = useSchemaTypes(siteId, env);
  const templates = useTemplates(siteId, env, 'form');

  const needle = search.trim().toLowerCase();
  const forms = (types.data ?? [])
    // Заказы магазина — в «Магазине».
    .filter((type) => type.purpose === 'form' && type.id !== scope?.shop?.orders?.id)
    .filter((type) => needle === '' || `${type.name} ${type.slug}`.toLowerCase().includes(needle));

  const create = () => {
    void navigate({ to: '/sites/$siteId/$env/forms/new', params: { siteId, env } });
  };

  const fromTemplate = () => {
    setTemplatesOpen(true);
  };

  const last = (type: TypeBody) =>
    type.last_record_at === undefined
      ? 'заявок ещё не было'
      : `последняя ${describeWhen(type.last_record_at)}`;

  const actions = shaper && (
    <>
      {(templates.data?.length ?? 0) > 0 && (
        <Button
          variant="outline"
          tone="neutral"
          iconLeft={<LayoutTemplate size={15} />}
          onClick={fromTemplate}
        >
          Из шаблона
        </Button>
      )}
      <Button iconLeft={<Plus size={15} />} onClick={create}>
        Новая форма
      </Button>
    </>
  );

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title="Формы"
        subtitle="Формы сайта и заявки с них: обратная связь, запись, отзывы. Новые заявки отмечены."
        actions={
          <>
            {forms.length > 0 && <ViewToggle value={view} onChange={setView} />}
            {actions}
          </>
        }
      />

      <TemplatesDialog
        siteID={siteId}
        env={env}
        purpose="form"
        open={templatesOpen}
        onOpenChange={setTemplatesOpen}
      />

      {types.isPending ? null : forms.length === 0 && needle === '' ? (
        <EmptyState
          icon={<Inbox />}
          title="Форм пока нет"
          description="Заведите форму — сайт будет присылать в неё заявки через API, а вы увидите их здесь. Быстрее всего — из шаблона «Форма обратной связи»."
          action={
            shaper ? (
              <Button
                iconLeft={<LayoutTemplate size={15} />}
                onClick={(templates.data?.length ?? 0) > 0 ? fromTemplate : create}
              >
                {(templates.data?.length ?? 0) > 0 ? 'Из шаблона' : 'Новая форма'}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <div className={catalog['bar']}>
            <CatalogSearch
              value={search}
              onChange={setSearch}
              placeholder="Поиск по названию или ключу"
            />
          </div>

          {view === 'grid' ? (
            <CatalogGrid>
              {forms.map((type) => (
                <CatalogCard
                  key={type.id}
                  link={{
                    to: '/sites/$siteId/$env/forms/$typeId',
                    params: { siteId, env, typeId: type.id },
                  }}
                  icon={<Inbox size={18} />}
                  title={type.name}
                  subtitle={type.slug}
                  badge={<Counter value={type.unread_count} tone="accent" />}
                  meta={
                    <>
                      <span>{pluralize(type.records_count, SUBMISSIONS)}</span>
                      <span>{last(type)}</span>
                      {type.api_access.create !== 'public' && <span>на паузе</span>}
                    </>
                  }
                />
              ))}
              {shaper && needle === '' && (
                <NewCard onClick={create}>
                  <Plus size={20} />
                  Новая форма
                </NewCard>
              )}
            </CatalogGrid>
          ) : (
            <CatalogTable head={['Название', 'Ключ', 'Заявок', 'Новых', 'Последняя']}>
              {forms.map((type) => (
                <tr key={type.id}>
                  <td>
                    <Link
                      to="/sites/$siteId/$env/forms/$typeId"
                      params={{ siteId, env, typeId: type.id }}
                    >
                      {type.name}
                    </Link>
                  </td>
                  <td className={catalog['mono']}>{type.slug}</td>
                  <td className={catalog['num']}>{type.records_count}</td>
                  <td className={catalog['num']}>{type.unread_count || '—'}</td>
                  <td>
                    {type.last_record_at === undefined ? '—' : describeWhen(type.last_record_at)}
                  </td>
                </tr>
              ))}
            </CatalogTable>
          )}
        </>
      )}
    </div>
  );
}
